(function () {
  'use strict';

  const PROP_PAIR = '_gift_pair';
  const PROP_AUTO_GIFT = '_auto_gift';

  const CLASSES = {
    loading: 'is-loading',
    added: 'is-added',
    visible: 'is-visible',
  };

  let isReconciling = false;
  let reconcileQueued = false;
  let reconcileTimer = null;

  function getPairId(item) {
    return item.properties?.[PROP_PAIR] || null;
  }

  function isAutoGift(item) {
    return item.properties?.[PROP_AUTO_GIFT] === 'true';
  }

  function getGiftHosts(button) {
    return [
      button.closest('quick-add-product'),
      button.closest('product-form'),
      button.closest('product-component'),
      button.closest('[data-section-type="product"]'),
    ].filter(Boolean);
  }

  function getGiftVariantId(button) {
    for (const host of getGiftHosts(button)) {
      if (host.dataset.hasGiftPair !== 'true') continue;

      const id = parseInt(host.dataset.giftVariantId, 10);
      if (!Number.isNaN(id)) return id;
    }

    return null;
  }

  function generatePairId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return 'gift-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);
  }

  function extractFormProperties(form) {
    const properties = {};
    const formData = new FormData(form);

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') {
        properties[match[1]] = value;
      }
    });

    return properties;
  }

  function getFormData(form) {
    return new FormData(form);
  }

  function getQuantity(form) {
    const qtyValue = getFormData(form).get('quantity');
    const qty = qtyValue ? parseInt(qtyValue, 10) : 1;
    return Number.isNaN(qty) || qty < 1 ? 1 : qty;
  }

  function escapeFormId(id) {
    if (window.CSS?.escape) return window.CSS.escape(id);
    return id.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function getVariantId(form) {
    const formData = getFormData(form);
    const formId = form.getAttribute('id');
    let idValue = formData.get('id');

    if (!idValue && formId) {
      const associatedInput = document.querySelector(`input[name="id"][form="${escapeFormId(formId)}"]`);
      idValue = associatedInput?.value || null;
    }

    if (!idValue && formId) {
      const associatedSelect = document.querySelector(`select[name="id"][form="${escapeFormId(formId)}"]`);
      idValue = associatedSelect?.value || null;
    }

    if (!idValue) return null;

    const id = parseInt(idValue, 10);
    return Number.isNaN(id) ? null : id;
  }

  function resolveVariantId(button, form) {
    const variantId = getVariantId(form);
    if (variantId) return variantId;

    const container =
      button.closest('[data-section-type="product"]') ||
      button.closest('[data-quick-add-modal]') ||
      button.closest('product-component');
    const productJsonEl = container?.querySelector('[data-product-json]');

    if (!productJsonEl?.textContent) return null;

    try {
      const product = JSON.parse(productJsonEl.textContent);
      const formData = getFormData(form);
      const selectedOptions = [];

      product.options.forEach((optionName, optionIndex) => {
        for (const [formKey, value] of formData.entries()) {
          if (formKey.startsWith(`options[${optionName}]`)) {
            selectedOptions[optionIndex] = value;
            break;
          }
        }
      });

      if (!selectedOptions.length && product.variants.length === 1) {
        return product.variants[0].id;
      }

      const variant = product.variants.find((entry) =>
        entry.options.every((option, optionIndex) => selectedOptions[optionIndex] === option)
      );

      return variant?.id || null;
    } catch (error) {
      return null;
    }
  }

  function shouldSkipForm(form) {
    if (form.querySelector('[type="file"]')) return true;

    const sellingPlan = form.querySelector('[name="selling_plan"]');
    if (sellingPlan && sellingPlan.value) return true;

    return false;
  }

  function getCartRoot() {
    return document.querySelector('cart-drawer') || document.querySelector('[data-cart-page]');
  }

  function setButtonLoading(button, loading) {
    if (!button) return;

    if (loading) {
      button.classList.add(CLASSES.loading);
      button.disabled = true;
    } else {
      button.classList.remove(CLASSES.loading);
      button.disabled = false;
    }
  }

  function showAddError(data, button) {
    const outerContainer =
      button.closest('[data-section-id]') ||
      button.closest('[data-quick-add-holder]') ||
      button.closest('[data-quick-add-modal]');
    const errorContainer = outerContainer?.querySelector('[data-cart-errors-container]');

    if (errorContainer && data) {
      let errorMessage = `${data.message}: ${data.description}`;

      if (data.message === data.description) {
        errorMessage = data.message;
      }

      errorContainer.innerHTML =
        '<div class="errors">' +
        errorMessage +
        '<button type="button" class="errors__close" data-close-error>' +
        '<svg aria-hidden="true" focusable="false" role="presentation" width="24px" height="24px" stroke-width="1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor" class="icon icon-cancel">' +
        '<path d="M6.758 17.243L12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></path>' +
        '</svg></button></div>';
      errorContainer.classList.add(CLASSES.visible);
    }

    document.dispatchEvent(
      new CustomEvent('theme:product:add-error', {
        detail: { response: data, button },
        bubbles: true,
      })
    );
  }

  async function addPairedItems(button, form, items) {
    const cart = getCartRoot();
    const quickAddHolder = button.closest('[data-quick-add-holder]');

    if (cart) cart.classList.add(CLASSES.loading);
    setButtonLoading(button, true);
    if (quickAddHolder) quickAddHolder.classList.add(CLASSES.visible);

    try {
      const response = await fetch(window.theme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      let data = null;

      try {
        data = await response.json();
      } catch (parseError) {
        showAddError({ message: 'Error', description: 'Unable to add to cart.' }, button);
        return;
      }

      if (!response.ok || data.status) {
        showAddError(data, button);
        return;
      }

      setButtonLoading(button, false);
      button.classList.add(CLASSES.added);

      button.dispatchEvent(
        new CustomEvent('theme:product:add', {
          detail: { response: data, button },
          bubbles: true,
        })
      );

      if (window.theme.settings.cartType === 'page') {
        window.location = window.theme.routes.cart_url;
        return;
      }

      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      scheduleReconcile();
    } catch (error) {
      showAddError({ message: 'Error', description: error.message || 'Unable to add to cart.' }, button);
    } finally {
      if (cart) cart.classList.remove(CLASSES.loading);
    }
  }

  function findExistingAutoGift(cartItems, giftVariantId) {
    return cartItems.find((item) => isAutoGift(item) && item.variant_id === giftVariantId) || null;
  }

  async function onCartAdd(event) {
    const button = event.detail?.button;
    if (!button) return;

    const giftVariantId = getGiftVariantId(button);
    if (!giftVariantId) return;

    const form = button.closest('form');
    if (!form || shouldSkipForm(form)) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const variantId = resolveVariantId(button, form);
    if (!variantId) return;

    event.stopImmediatePropagation();

    const quantity = getQuantity(form);
    const properties = extractFormProperties(form);

    let existingGift = null;

    try {
      const cart = await fetchCart();
      existingGift = findExistingAutoGift(cart.items, giftVariantId);
    } catch (error) {
      // If cart fetch fails, fall back to adding a single gift.
    }

    // Same gift product is only given once, regardless of trigger quantity or repeat adds.
    if (existingGift) {
      const pairId = getPairId(existingGift) || generatePairId();
      properties[PROP_PAIR] = pairId;

      addPairedItems(button, form, [{ id: variantId, quantity, properties }]);
      return;
    }

    const pairId = generatePairId();
    properties[PROP_PAIR] = pairId;

    const items = [
      { id: variantId, quantity, properties },
      {
        id: giftVariantId,
        quantity: 1,
        properties: {
          [PROP_PAIR]: pairId,
          [PROP_AUTO_GIFT]: 'true',
        },
      },
    ];

    addPairedItems(button, form, items);
  }

  function computeGiftVariantAdjustments(cartItems) {
    const changes = [];
    const giftVariantMap = new Map();

    cartItems.forEach((item, index) => {
      if (!isAutoGift(item)) return;

      const variantId = item.variant_id;
      if (!giftVariantMap.has(variantId)) {
        giftVariantMap.set(variantId, []);
      }

      giftVariantMap.get(variantId).push({ item, line: index + 1 });
    });

    giftVariantMap.forEach((gifts) => {
      const relatedPairIds = new Set(gifts.map(({ item }) => getPairId(item)).filter(Boolean));
      const hasTrigger = cartItems.some(
        (item) => !isAutoGift(item) && relatedPairIds.has(getPairId(item))
      );
      const desiredQty = hasTrigger ? 1 : 0;

      gifts.forEach(({ item, line }, giftIndex) => {
        if (giftIndex === 0) {
          if (item.quantity !== desiredQty) {
            changes.push({ line, quantity: desiredQty });
          }
        } else if (item.quantity !== 0) {
          // Keep only one auto-gift line per gift variant.
          changes.push({ line, quantity: 0 });
        }
      });
    });

    return changes;
  }

  async function fetchCart() {
    const response = await fetch(window.theme.routes.cart_url + '.js', {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  }

  async function changeLine(line, quantity) {
    const response = await fetch(window.theme.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });

    if (!response.ok) {
      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        // ignore parse errors
      }
      throw new Error(data.description || 'Cart update failed');
    }

    return response.json();
  }

  async function reconcileCart() {
    if (isReconciling) {
      reconcileQueued = true;
      return;
    }

    isReconciling = true;

    try {
      const cart = await fetchCart();
      const changes = computeGiftVariantAdjustments(cart.items);

      if (!changes.length) return;

      const deduped = new Map();
      changes.forEach((change) => deduped.set(change.line, change.quantity));

      const sortedChanges = Array.from(deduped.entries())
        .map(([line, quantity]) => ({ line, quantity }))
        .sort((a, b) => b.line - a.line);

      for (const change of sortedChanges) {
        await changeLine(change.line, change.quantity);
      }

      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    } catch (error) {
      console.warn('Cart auto-gift reconcile failed:', error);
    } finally {
      isReconciling = false;

      if (reconcileQueued) {
        reconcileQueued = false;
        scheduleReconcile();
      }
    }
  }

  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcileCart, 50);
  }

  function init() {
    if (!window.theme?.routes?.cart_add_url) return;

    document.addEventListener('theme:cart:add', onCartAdd, true);
    document.addEventListener('theme:cart:change', scheduleReconcile);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
